import React, { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CTASection from "../components/CTASection";
import HeroNatural from "../components/HeroNatural";
import { usePageTitle } from "../hooks/usePageTitle";
import {
	faGuitar,
	faMicrophone,
	faMusic,
	faCompactDisc,
	faTheaterMasks,
	faBasketball,
	faPalette,
	faCat,
	faGamepad,
	faBuilding,
	faPlane,
	faUtensils,
	faMagnifyingGlass,
	faChevronRight,
	faTicketAlt,
	faCreditCard,
	faClock,
	faLocationDot,
	faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

interface Event {
	id: number;
	title: string;
	artist: string;
	date: string;
	venue: string;
	imageUrl: string;
	price?: string;
	category: string;
	ranking?: number;
	attendees?: number;
}

const HomePage: React.FC = () => {
	const navigate = useNavigate();
	const searchInputRef = useRef<HTMLInputElement>(null);

	usePageTitle("ホーム");

	const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		const keyword = formData.get("keyword");

		if (keyword) {
			navigate(`/events?keyword=${encodeURIComponent(keyword.toString())}`);
		} else {
			navigate("/events");
		}
	};

	const featuredEvents: Event[] = [
		{
			id: 1,
			title: "Summer Rock Festival 2024",
			artist: "Various Artists",
			date: "2024-07-20",
			venue: "Zepp Tokyo",
			imageUrl: "/images/events/summer-rock.webp",
			price: "¥8,800〜",
			category: "ロック",
			ranking: 1,
			attendees: 22246,
		},
		{
			id: 2,
			title: "クラシックの夕べ",
			artist: "東京フィルハーモニー管弦楽団",
			date: "2024-08-15",
			venue: "サントリーホール",
			imageUrl: "/images/events/classical.webp",
			price: "¥5,500〜",
			category: "クラシック",
			ranking: 2,
			attendees: 8965,
		},
		{
			id: 3,
			title: "J-POP LIVE 2024",
			artist: "MATAKI",
			date: "2024-09-01",
			venue: "横浜アリーナ",
			imageUrl: "/images/events/jpop.webp",
			price: "¥7,700〜",
			category: "ポップス",
			ranking: 3,
			attendees: 15789,
		},
	];

	const categories = [
		{ name: "クラシック", icon: faMusic },
		{ name: "演劇・お笑い", icon: faTheaterMasks },
		{ name: "音楽", icon: faGuitar },
		{ name: "アート・文化", icon: faPalette },
		{ name: "アニメ", icon: faCat },
		{ name: "ゲーム", icon: faGamepad },
		{ name: "展示・講演", icon: faBuilding },
		{ name: "旅・観光", icon: faPlane },
		{ name: "食・グルメ", icon: faUtensils },
		{ name: "スポーツ", icon: faBasketball },
		{ name: "ポップス", icon: faMicrophone },
		{ name: "その他", icon: faCompactDisc },
	];

	return (
		<div className="overflow-x-hidden">
			{/* ==================== Hero Section ==================== */}
			<section className="relative isolate min-h-[75vh] sm:min-h-[70vh] flex items-center overflow-hidden">
				<HeroNatural />

				<div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 md:py-40">
					<div className="max-w-2xl">
						<p className="text-xs sm:text-sm font-medium tracking-[0.2em] uppercase text-blue-600/60 dark:text-blue-400/50 mb-6">
							b+ ticket
						</p>

						<h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] font-bold leading-[1.08] tracking-tight text-gray-900 dark:text-white mb-6">
							ライブチケットを、
							<br />
							<span className="text-blue-600 dark:text-blue-400">もっと手軽に。</span>
						</h1>

						<p className="text-lg sm:text-xl text-gray-500 dark:text-gray-400 max-w-md leading-relaxed mb-10">
							お気に入りのアーティストのライブチケットを、手数料無料で簡単に購入。
						</p>

						{/* Search Box */}
						<form
							onSubmit={handleSearch}
							className="max-w-lg"
						>
							<div className="relative flex items-center bg-white dark:bg-gray-800/80 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700/50 focus-within:border-blue-400 dark:focus-within:border-blue-500/50 focus-within:shadow-blue-100/50 dark:focus-within:shadow-blue-900/20 focus-within:shadow-md transition-all">
								<div className="absolute left-4 text-gray-300 dark:text-gray-600">
									<FontAwesomeIcon
										icon={faMagnifyingGlass}
										className="h-4 w-4"
									/>
								</div>
								<input
									ref={searchInputRef}
									type="text"
									name="keyword"
									placeholder="イベント名、アーティスト、会場で検索"
									className="w-full pl-11 pr-24 py-4 bg-transparent text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none rounded-xl"
								/>
								<button
									type="submit"
									className="absolute right-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-colors"
								>
									<span className="hidden sm:inline">検索</span>
									<FontAwesomeIcon
										icon={faArrowRight}
										className="h-4 w-4 sm:hidden"
									/>
								</button>
							</div>
						</form>
					</div>
				</div>
			</section>

			{/* ==================== Categories Section ==================== */}
			<section className="py-10 sm:py-14 px-4 sm:px-6 lg:px-8 border-t border-gray-100 dark:border-gray-800/50">
				<div className="max-w-6xl mx-auto">
					<div className="flex items-center gap-4 mb-6">
						<h2 className="text-base font-medium text-gray-900 dark:text-white whitespace-nowrap">
							カテゴリー
						</h2>
						<div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
					</div>

					<div className="flex flex-wrap gap-2">
						{categories.map((category) => (
							<Link
								key={category.name}
								to={`/events?category=${category.name}`}
								className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-500 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors bg-white dark:bg-transparent"
							>
								<FontAwesomeIcon
									icon={category.icon}
									className="h-3 w-3 text-gray-400 dark:text-gray-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors"
								/>
								{category.name}
							</Link>
						))}
					</div>
				</div>
			</section>

			{/* ==================== Featured Events Section ==================== */}
			<section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
				<div className="max-w-6xl mx-auto">
					<div className="flex items-end justify-between mb-10 sm:mb-12">
						<div>
							<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-blue-600/50 dark:text-blue-400/40 mb-2">
								Pick Up
							</p>
							<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
								注目のイベント
							</h2>
						</div>
						<Link
							to="/events"
							className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
						>
							<span>すべて見る</span>
							<FontAwesomeIcon
								icon={faChevronRight}
								className="h-2.5 w-2.5"
							/>
						</Link>
					</div>

					{/* Asymmetric grid - first card large */}
					<div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
						{/* Main featured event - large */}
						<Link
							to={`/events/${featuredEvents[0].id}`}
							className="group lg:col-span-7"
						>
							<div className="bg-white dark:bg-gray-900/50 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-900/50 hover:shadow-lg hover:shadow-blue-50 dark:hover:shadow-blue-950/20 transition-all duration-300">
								<div className="relative aspect-[16/10] overflow-hidden">
									<img
										src={featuredEvents[0].imageUrl}
										alt={featuredEvents[0].title}
										className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
										loading="lazy"
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
									{featuredEvents[0].price && (
										<div className="absolute bottom-4 left-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm text-gray-900 dark:text-white text-sm font-medium px-3 py-1.5 rounded-lg">
											{featuredEvents[0].price}
										</div>
									)}
									<div className="absolute top-4 left-4 bg-blue-600/90 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-md">
										#{featuredEvents[0].ranking}
									</div>
								</div>

								<div className="p-5 sm:p-6">
									<div className="flex items-center gap-2 mb-2">
										<span className="inline-block text-[11px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 px-2 py-0.5 rounded">
											{featuredEvents[0].category}
										</span>
										{featuredEvents[0].attendees && (
											<span className="text-[11px] text-gray-400 dark:text-gray-600">
												{featuredEvents[0].attendees.toLocaleString()}人が注目
											</span>
										)}
									</div>
									<h3 className="font-bold text-xl sm:text-2xl text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
										{featuredEvents[0].title}
									</h3>
									<p className="text-base text-gray-500 dark:text-gray-400 mb-4">
										{featuredEvents[0].artist}
									</p>
									<div className="flex items-center gap-5 text-sm text-gray-400 dark:text-gray-500">
										<div className="flex items-center gap-1.5">
											<FontAwesomeIcon icon={faClock} className="h-3 w-3" />
											<span>{featuredEvents[0].date}</span>
										</div>
										<div className="flex items-center gap-1.5">
											<FontAwesomeIcon icon={faLocationDot} className="h-3 w-3" />
											<span>{featuredEvents[0].venue}</span>
										</div>
									</div>
								</div>
							</div>
						</Link>

						{/* Secondary events - stacked */}
						<div className="lg:col-span-5 flex flex-col gap-5">
							{featuredEvents.slice(1).map((event) => (
								<Link
									key={event.id}
									to={`/events/${event.id}`}
									className="group flex-1"
								>
									<div className="h-full bg-white dark:bg-gray-900/50 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-900/50 hover:shadow-lg hover:shadow-blue-50 dark:hover:shadow-blue-950/20 transition-all duration-300 flex flex-row">
										{/* Image */}
										<div className="relative w-32 sm:w-40 flex-shrink-0 overflow-hidden">
											<img
												src={event.imageUrl}
												alt={event.title}
												className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
												loading="lazy"
											/>
											{event.ranking && (
												<div className="absolute top-2 left-2 bg-blue-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded">
													#{event.ranking}
												</div>
											)}
										</div>

										{/* Content */}
										<div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
											<div>
												<span className="inline-block text-[10px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 px-1.5 py-0.5 rounded mb-1.5">
													{event.category}
												</span>
												<h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2 mb-1">
													{event.title}
												</h3>
												<p className="text-sm text-gray-500 dark:text-gray-400">
													{event.artist}
												</p>
											</div>
											<div className="flex items-center justify-between mt-3">
												<div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
													<div className="flex items-center gap-1">
														<FontAwesomeIcon icon={faClock} className="h-2.5 w-2.5" />
														<span>{event.date}</span>
													</div>
													<div className="flex items-center gap-1">
														<FontAwesomeIcon icon={faLocationDot} className="h-2.5 w-2.5" />
														<span className="truncate">{event.venue}</span>
													</div>
												</div>
												{event.price && (
													<span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap ml-2">
														{event.price}
													</span>
												)}
											</div>
										</div>
									</div>
								</Link>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* ==================== CTA Section ==================== */}
			<CTASection />

			{/* ==================== How It Works Section ==================== */}
			<section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-gray-50/80 dark:bg-gray-900/30">
				<div className="max-w-5xl mx-auto">
					<div className="text-center mb-16 sm:mb-20">
						<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-blue-600 dark:text-blue-400 mb-3">
							How it works
						</p>
						<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
							3ステップで購入完了
						</h2>
						<p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
							面倒な手続きは一切なし。見つけて、選んで、購入するだけ。
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
						{[
							{
								num: "01",
								title: "イベントを探す",
								desc: "豊富なカテゴリや検索から、気になるイベントを見つけましょう",
								icon: faMusic,
							},
							{
								num: "02",
								title: "チケットを選ぶ",
								desc: "席種と枚数を選んで、ワンタップで簡単に予約完了",
								icon: faTicketAlt,
							},
							{
								num: "03",
								title: "支払い & 受け取り",
								desc: "安全な決済後、電子チケットがすぐに届きます",
								icon: faCreditCard,
							},
						].map((step, index) => (
							<div key={index} className="relative text-center group">
								{/* Step number background */}
								<div className="text-7xl sm:text-8xl font-bold text-blue-100 dark:text-blue-900/30 leading-none mb-4 select-none">
									{step.num}
								</div>
								{/* Icon */}
								<div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-600 dark:bg-blue-500 text-white mb-5 -mt-10 relative">
									<FontAwesomeIcon icon={step.icon} className="h-6 w-6" />
								</div>
								<h3 className="font-bold text-lg sm:text-xl text-gray-900 dark:text-white mb-3">
									{step.title}
								</h3>
								<p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs mx-auto">
									{step.desc}
								</p>
							</div>
						))}
					</div>

					<div className="text-center mt-16 sm:mt-20">
						<Link
							to="/events"
							className="inline-flex items-center gap-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-8 py-4 rounded-xl text-base font-medium transition-colors group"
						>
							<span>イベントを探す</span>
							<FontAwesomeIcon
								icon={faArrowRight}
								className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
							/>
						</Link>
					</div>
				</div>
			</section>
		</div>
	);
};

export default HomePage;
